import { Box, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import PercentileChip from "../PercentileChip";
import { CONDENSED_FONT_FAMILY, DISPLAY_FONT_FAMILY } from "../constants";
import { nth } from "../utils";

export interface TeamProfileCell {
  value: number;
  /** MAGNITUDE rank (season reports): 1 = highest raw value in the league */
  rank?: number;
  /** MAGNITUDE percentile (game reports): 100 = highest raw value among the
   * population's single-game team performances */
  percentile?: number;
  rankPopulation?: number;
  /** Good/bad judgment, independent of the score; absent = style row */
  direction?: "high_good" | "low_good";
}

export interface TeamProfileRow {
  label: string;
  /** "share" = tendency, "quality" = good/bad stat (informational; every
   * scored cell renders the same chip) */
  kind: "share" | "quality";
  off?: TeamProfileCell | null;
  def?: TeamProfileCell | null;
}

export interface TeamProfileData {
  rows: TeamProfileRow[];
}

const HEAD_SX = {
  fontFamily: CONDENSED_FONT_FAMILY,
  fontSize: "0.782rem",
  fontWeight: 700,
  textTransform: "uppercase" as const,
  letterSpacing: "0.06em",
  color: "text.secondary",
  borderBottom: "2px solid #111",
  px: 0.5,
  py: 0.25,
  textAlign: "center" as const,
};
// Rows carry the report's table hairlines (the borderless first cut read as
// floating text — user call 2026-09-15).
const ROW_BORDER = "1px solid #e6e4e1";
const LABEL_SX = {
  fontFamily: CONDENSED_FONT_FAMILY,
  fontSize: "0.862rem",
  fontWeight: 700,
  px: 0.5,
  py: 0.35,
  whiteSpace: "nowrap" as const,
  borderBottom: ROW_BORDER,
};
const CELL_SX = {
  fontFamily: CONDENSED_FONT_FAMILY,
  fontSize: "0.862rem",
  px: 0.5,
  py: 0.35,
  textAlign: "center" as const,
  whiteSpace: "nowrap" as const,
  borderBottom: ROW_BORDER,
};

/** Display precision: every strip stat is one decimal except these. */
const DECIMALS: Record<string, number> = { "Crashers on 3s": 2, "AST/TOV": 2 };

/**
 * Per-stat ordinal wording overrides — presentation only, the blob is
 * unchanged. Defensive Trans Rate reads by "most" ("4th most", not "27th
 * least"): Trevor + Jacob, 2026-10-07. Its color still follows goodness, so
 * a lot of transition allowed stays blue. Every other cell keeps the
 * goodness-first ordinal.
 */
const READS_BY_MOST: Record<string, { off?: boolean; def?: boolean }> = {
  "Trans Rate": { def: true },
};

/** Salience bands: only the five best and five worst of 30 color (user call
 * 2026-09-15); a percentile colors in the same share at each end. */
const BAND_RANKS = 5;
const BAND_SHARE = BAND_RANKS / 30;

function ProfileCell({
  cell,
  decimals = 1,
  readsByMost = false,
}: {
  cell?: TeamProfileCell | null;
  decimals?: number;
  readsByMost?: boolean;
}) {
  // A side the row has no stat for stays empty (no stray "—").
  if (!cell) return <TableCell sx={CELL_SX} />;
  const pop = cell.rankPopulation ?? 30;
  // Goodness-first ordinals (user call 2026-09-15): every cell declares its
  // relevant/good pole; 1 is always the good/relevant end. When the HIGH
  // pole is relevant the bare ordinal reads naturally ("7th" = 7th most, no
  // label). When the LOW pole is relevant the ordinal counts from the
  // bottom and says so ("2nd least"). Stored `rank` / `percentile` are
  // always magnitude.
  const lowPole = cell.direction === "low_good";
  let numeral: string | undefined;
  let hover = "";
  // 0-100, 100 = best (or the most, on a style row): drives the color
  let goodness: number | undefined;
  // The goodness-first ordinal behind a colored rank cell: the bucketed color modes bucket on it
  let colorOrd: number | undefined;
  if (cell.rank !== undefined) {
    const ord = lowPole ? pop - cell.rank + 1 : cell.rank;
    if (readsByMost) {
      numeral = `${nth(cell.rank)} most`;
      hover = `${nth(cell.rank)} most of ${pop}`;
    } else {
      numeral = lowPole ? `${nth(ord)} least` : nth(ord);
      hover = `${nth(ord)} ${lowPole ? "least" : "most"} of ${pop}`;
    }
    if (pop > 1 && (ord <= BAND_RANKS || ord >= pop - BAND_RANKS + 1)) {
      goodness = ((pop - ord) / (pop - 1)) * 100;
      colorOrd = ord;
    }
  } else if (cell.percentile !== undefined) {
    // Game reports: percentiles read like every other game cell, higher =
    // better (a style row: higher = more).
    const good = lowPole ? 100 - cell.percentile : cell.percentile;
    numeral = String(Math.round(good));
    hover = readsByMost
      ? `More than ${Math.round(cell.percentile)}% of league games (${pop} team games)`
      : `${nth(Math.round(good))} percentile${lowPole ? ", lower is better" : ""} · ${pop} team games`;
    if (good >= 100 * (1 - BAND_SHARE) || good <= 100 * BAND_SHARE) goodness = good;
  }
  return (
    <TableCell sx={CELL_SX}>
      <Tooltip title={hover} enterDelay={300} placement="top" arrow>
        <span>
          <PercentileChip
            value={Number(cell.value).toFixed(decimals)}
            numeral={numeral}
            percentile={goodness ?? 50}
            rank={colorOrd}
            rankPopulation={pop}
            // Uncolored outside the tails, in every color mode (a bucketed p50 would otherwise take a tint)
            background={goodness === undefined ? "transparent" : undefined}
            dataFillPct={goodness ?? 50}
            fontSize="0.862rem"
            autoNumeralWidth
          />
        </span>
      </Tooltip>
    </TableCell>
  );
}

function HalfTable({ rows }: { rows: TeamProfileRow[] }) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ ...HEAD_SX, textAlign: "left" }} />
          <TableCell sx={HEAD_SX}>Off</TableCell>
          <TableCell sx={HEAD_SX}>Def</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.label}>
            <TableCell sx={LABEL_SX}>{row.label}</TableCell>
            <ProfileCell
              cell={row.off}
              decimals={DECIMALS[row.label] ?? 1}
              readsByMost={Boolean(READS_BY_MOST[row.label]?.off)}
            />
            <ProfileCell
              cell={row.def}
              decimals={DECIMALS[row.label] ?? 1}
              readsByMost={Boolean(READS_BY_MOST[row.label]?.def)}
            />
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/**
 * Overview "Team Profile" strip (2026-09-10): the identity card in the slot
 * the shot charts vacated — off/def columns of style + four-factor stats
 * with league ranks (season) or single-game percentiles (game reports,
 * TR4), split into two side-by-side halves to use the width.
 */
export default function TeamProfileStrip({ profile }: { profile?: TeamProfileData | null }) {
  const rows = profile?.rows ?? [];
  if (!rows.length) return null;
  const mid = Math.ceil(rows.length / 2);
  return (
    <Box>
      <Typography
        sx={{
          fontFamily: DISPLAY_FONT_FAMILY,
          fontWeight: 700,
          fontSize: "1.265rem",
          textTransform: "uppercase",
          letterSpacing: "0.02em",
          mb: 0.25,
        }}
      >
        Team Profile
      </Typography>
      <Stack direction="row" spacing={4} alignItems="flex-start">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <HalfTable rows={rows.slice(0, mid)} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, borderLeft: "1px solid #cfccc8", pl: 4 }}>
          <HalfTable rows={rows.slice(mid)} />
        </Box>
      </Stack>
    </Box>
  );
}
